// @ts-check
/**
 * Cálculo de totales de un gasto/factura con descuento.
 *
 * Regla fiscal UY: el descuento se aplica sobre el NETO (subtotal) y el IVA se
 * RECALCULA sobre el subtotal ya descontado. Ej.: facturan $1000 + IVA 22% con
 * 10% de descuento → neto 900, IVA 198, total 1098.
 *
 * `amount` (total) es el único campo que consume el resto del sistema (cierre,
 * fondos), así que lo importante es que el descuento baje `amount`.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * @param {Object} p
 * @param {number} p.subtotalBruto  Subtotal NETO antes del descuento.
 * @param {number} [p.ivaBruto]     IVA en $ correspondiente al subtotalBruto (se usa para derivar la tasa).
 * @param {number} [p.ivaTasa]      Tasa de IVA en % (si se conoce; tiene prioridad sobre ivaBruto).
 * @param {"porcentaje"|"monto"|null} [p.descuentoTipo]
 * @param {number} [p.descuentoValor]
 * @returns {{subtotalBruto:number, ivaTasa:number, descuentoMonto:number, subtotal:number, iva:number, amount:number}}
 */
export function computeExpenseTotals({ subtotalBruto, ivaBruto, ivaTasa, descuentoTipo, descuentoValor }) {
  const bruto = Math.max(0, Number(subtotalBruto) || 0);

  // Tasa de IVA: explícita, o derivada de ivaBruto/subtotalBruto, o 0.
  let tasa = ivaTasa != null && ivaTasa !== ""
    ? Number(ivaTasa)
    : (bruto > 0 && ivaBruto != null ? (Number(ivaBruto) / bruto) * 100 : 0);
  if (!isFinite(tasa) || tasa < 0) tasa = 0;

  // Descuento sobre el neto (capado a [0, bruto]).
  let descuentoMonto = 0;
  if (descuentoTipo === "porcentaje") {
    const pct = Math.min(100, Math.max(0, Number(descuentoValor) || 0));
    descuentoMonto = bruto * (pct / 100);
  } else if (descuentoTipo === "monto") {
    descuentoMonto = Math.min(bruto, Math.max(0, Number(descuentoValor) || 0));
  }
  descuentoMonto = round2(descuentoMonto);

  const subtotal = round2(bruto - descuentoMonto);
  const iva = round2(subtotal * (tasa / 100));
  const amount = round2(subtotal + iva);

  return { subtotalBruto: round2(bruto), ivaTasa: round2(tasa), descuentoMonto, subtotal, iva, amount };
}

/** ¿Hay un descuento efectivo aplicado? */
export function tieneDescuento(descuentoTipo, descuentoValor) {
  return (descuentoTipo === "porcentaje" || descuentoTipo === "monto") && Number(descuentoValor) > 0;
}
